import { FlashbarProps } from '@cloudscape-design/components';
import { YAMLException } from 'js-yaml';

export interface ErrorResponse {
  message: string;
  response: { data: object };
}

export function instanceOfErrorResponse(v: object): v is ErrorResponse {
  return (
    'message' in v &&
    'response' in v &&
    v.response !== null &&
    typeof v.response === 'object' &&
    'data' in v.response
  );
}

export function isYAMLException(error: unknown): error is YAMLException {
  return (
    error !== null &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name: string }).name === 'YAMLException'
  );
}

export interface ErrorHandlerOptions {
  context: string;
  successMessage?: string;
  jsonValidationErrorMessage?: string;
  unknownErrorMessage?: string;
}

export interface ErrorHandlerResult {
  flashbarItems: FlashbarProps.MessageDefinition[];
  success: boolean;
}

/**
 * Creates standardized error handling for upgrade operations
 * @param error - The error object to handle
 * @param options - Configuration options for error messages
 * @returns Flashbar items and success status
 */
export function handleUpgradeError(
  error: unknown,
  options: ErrorHandlerOptions,
): ErrorHandlerResult {
  const {
    context,
    jsonValidationErrorMessage = 'The uploaded file contains invalid JSON. Please check the file format and try again.',
    unknownErrorMessage = `Failed to ${context}, an unexpected error occurred. Please try again.`,
  } = options;

  if (error instanceof SyntaxError) {
    return {
      flashbarItems: [
        {
          type: 'error',
          dismissible: true,
          dismissLabel: 'Dismiss',
          content: jsonValidationErrorMessage,
          id: 'json_error',
        },
      ],
      success: false,
    };
  }

  // Handle YAML parsing errors
  if (isYAMLException(error)) {
    const lineInfo = error.mark
      ? ` at line ${error.mark.line + 1}, column ${error.mark.column + 1}`
      : '';
    return {
      flashbarItems: [
        {
          type: 'error',
          dismissible: true,
          dismissLabel: 'Dismiss',
          content: `Failed to ${context}: Invalid YAML syntax${lineInfo}. ${error.reason || ''}`,
          id: 'yaml_error',
        },
      ],
      success: false,
    };
  }

  if (
    error !== null &&
    typeof error === 'object' &&
    instanceOfErrorResponse(error)
  ) {
    return {
      flashbarItems: [
        {
          type: 'error',
          dismissible: true,
          dismissLabel: 'Dismiss',
          content: (
            <>
              Failed to {context}: {error.message.toString()} -{' '}
              {JSON.stringify(error.response.data)}
            </>
          ),
          id: 'api_error',
        },
      ],
      success: false,
    };
  }

  // Handle standard Error objects (validation errors, etc.)
  if (error instanceof Error) {
    return {
      flashbarItems: [
        {
          type: 'error',
          dismissible: true,
          dismissLabel: 'Dismiss',
          content: `Failed to ${context}: ${error.message}`,
          id: 'validation_error',
        },
      ],
      success: false,
    };
  }

  return {
    flashbarItems: [
      {
        type: 'error',
        dismissible: true,
        dismissLabel: 'Dismiss',
        content: unknownErrorMessage,
        id: 'unknown_error',
      },
    ],
    success: false,
  };
}

/**
 * Creates a success message for upgrade operations
 * @param message - The success message to display
 * @returns Flashbar items for success
 */
export function createSuccessMessage(message: string): ErrorHandlerResult {
  return {
    flashbarItems: [
      {
        type: 'success',
        dismissible: true,
        dismissLabel: 'Dismiss',
        onDismiss: () => {},
        content: message,
        id: 'success_message',
      },
    ],
    success: true,
  };
}

/**
 * Hook-like utility for managing flashbar items with error handling
 * @param setFlashbarItems - State setter for flashbar items
 * @returns Object with error handling and success message functions
 */
export function useUpgradeErrorHandler(
  setFlashbarItems: (items: FlashbarProps.MessageDefinition[]) => void,
) {
  const handleError = (error: unknown, options: ErrorHandlerOptions) => {
    const result = handleUpgradeError(error, options);
    const itemsWithDismiss = result.flashbarItems.map((item) => ({
      ...item,
      onDismiss: () => setFlashbarItems([]),
    }));
    setFlashbarItems(itemsWithDismiss);
    return result.success;
  };

  const showSuccess = (message: string) => {
    const result = createSuccessMessage(message);
    const itemsWithDismiss = result.flashbarItems.map((item) => ({
      ...item,
      onDismiss: () => setFlashbarItems([]),
    }));
    setFlashbarItems(itemsWithDismiss);
  };

  return {
    handleError,
    showSuccess,
  };
}
